import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { getMyProgress } from '../services/api';
import ProgressChart from './ProgressChart';
import { FEATURES } from '../config/features';
import Larry, { LarryTip } from './Larry';
import usePageTitle from '../hooks/usePageTitle';

const METRICS = [
  { id: 'pace_score', label: 'Pace', icon: '⚡' },
  { id: 'wpm', label: 'WPM', icon: '🎤' },
  { id: 'filler_free', label: 'Filler-Free', icon: '✨' },
  { id: 'vocab_score', label: 'Vocabulary', icon: '📚' },
  { id: 'sentence_score', label: 'Sentences', icon: '📐' },
];

const TREND_STYLE = {
  improving: { icon: '↑', color: 'var(--color-success)', borderColor: 'var(--color-success-border)' },
  declining: { icon: '↓', color: 'var(--color-danger)', borderColor: 'var(--color-danger-border)' },
  stable:    { icon: '→', color: 'var(--text-muted)', borderColor: 'var(--border-subtle)' },
};

function SummaryCard({ label, value, suffix, trend, icon }) {
  const t = TREND_STYLE[trend] || TREND_STYLE.stable;
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: 'var(--bg-input)',
        border: '1px solid var(--border-subtle)',
        borderLeft: `3px solid ${t.borderColor}`,
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-extrabold uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>{label}</p>
        <span className="text-lg">{icon}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-black tabular-nums font-mono" style={{ color: 'var(--text-primary)' }}>{value}{suffix}</span>
        {trend && (
          <span
            className="text-xs font-extrabold px-1.5 py-0.5 rounded-pill"
            style={{ background: 'var(--accent-dim)', color: t.color }}
          >
            {t.icon}
          </span>
        )}
      </div>
    </div>
  );
}

function generateInsights(progress) {
  if (!progress || progress.total_speeches < 2) return [];
  const { summary, trends } = progress;
  const insights = [];

  if (trends.filler_rate === 'improving') {
    insights.push("Filler words trending down — you're crushing it!");
  } else if (trends.filler_rate === 'declining') {
    insights.push('Fillers crept up. Pause instead. Silence lands harder than any filler.');
  }

  if (trends.vocab_score === 'improving') {
    insights.push('Your vocabulary is getting richer. The audience feels the difference.');
  }

  if (trends.pace_score === 'improving') {
    insights.push('Pace consistency improving — you sound more controlled and deliberate.');
  }

  if (summary.avg_wpm > 170) {
    insights.push("You're speaking fast. Slow down. Let words breathe.");
  } else if (summary.avg_wpm < 110) {
    insights.push('Your pace is relaxed. Push it a little. The room will lean in.');
  }

  if (trends.sentence_score === 'improving') {
    insights.push('Sentence variety improving — your delivery is becoming more dynamic.');
  }

  return insights;
}

export default function ProgressPage() {
  usePageTitle('My Progress');
  const [progress, setProgress] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('pace_score');
  const [chartMode, setChartMode] = useState('line');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProgress = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMyProgress();
      setProgress(res?.progress || null);
    } catch (err) {
      setError(err.message || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center py-16 gap-3">
        <Larry mood="thinking" size={64} message="Pulling your data…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <Larry mood="sad" size={64} message="Couldn't load your progress." />
        <p className="text-sm" style={{ color: 'var(--text-faint)' }}>{error}</p>
        <button
          onClick={() => { fetchedRef.current = false; loadProgress(); }}
          className="btn-bouncy text-sm px-5 py-2"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!progress || progress.total_speeches === 0) {
    return (
      <div className="card-paper p-8 flex flex-col items-center text-center gap-3">
        <Larry mood="excited" size={72} message="No data yet." />
        <p className="text-sm font-medium" style={{ color: 'var(--text-faint)' }}>
          Your progress chart will appear after your first speech.
        </p>
        {/* Both destinations are flag-gated; with neither module on there is
            nowhere for this CTA to go, so it drops out entirely. */}
        {(FEATURES.SCHEDULING || FEATURES.COACHING) && (
          <Link to={FEATURES.SCHEDULING ? '/calendar' : '/analyze'} className="btn-bouncy text-sm px-5 py-2 mt-1">
            {FEATURES.SCHEDULING ? 'Apply to Speak →' : 'Analyze a speech →'}
          </Link>
        )}
      </div>
    );
  }

  const { summary, trends, data_points } = progress;
  const insights = generateInsights(progress);

  return (
    <div className="space-y-6 stagger-in">
      <h1 className="sr-only">Growth</h1>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SummaryCard
          label="Speeches"
          value={progress.total_speeches}
          suffix=""
          icon="🎤"
        />
        <SummaryCard
          label="Avg WPM"
          value={Math.round(summary.avg_wpm)}
          suffix=""
          trend={trends.wpm}
          icon="⚡"
        />
        <SummaryCard
          label="Fillers"
          value={Number.isFinite(summary.avg_filler_rate) ? summary.avg_filler_rate.toFixed(1) : '0'}
          suffix="%"
          trend={trends.filler_rate}
          icon="✨"
        />
        <SummaryCard
          label="Vocabulary"
          value={summary.avg_vocab_score > 0 ? Math.round(summary.avg_vocab_score) : '—'}
          suffix={summary.avg_vocab_score > 0 ? '' : ''}
          trend={trends.vocab_score}
          icon="📚"
        />
      </div>

      {/* Chart mode toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setChartMode('line')}
          className={clsx('pill-tab', chartMode === 'line' && 'active')}
        >
          📈 Timeline
        </button>
      </div>

      {/* Metric tabs — only shown in line mode */}
      {chartMode === 'line' && (
        <div className="flex gap-2 flex-wrap">
          {METRICS.map(m => (
            <button
              key={m.id}
              onClick={() => setSelectedMetric(m.id)}
              className={clsx('pill-tab flex items-center gap-1.5', selectedMetric === m.id && 'active')}
            >
              <span className="text-sm">{m.icon}</span>
              {m.label}
            </button>
          ))}
        </div>
      )}

      {/* Chart */}
      <div className="card-paper p-5">
        <ProgressChart
          data={data_points}
          metricKey={selectedMetric}
          mode={chartMode}
        />
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div
          className="rounded-2xl p-5 space-y-3"
          style={{
            background: 'var(--bg-input)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <LarryTip tip="Here's what I noticed!" />
          </div>
          {insights.map((text, i) => (
            <p key={i} className="text-sm font-medium flex items-start gap-2" style={{ color: 'var(--text-secondary)' }}>
              <span className="mt-0.5" style={{ color: 'var(--color-success)' }}>★</span>
              <span>{text}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
